// components/Enrollment/BatchEnrollmentRequests.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The "Requests" tab on a batch page — deciding who gets a seat, from the place
// where the seat count is visible.
//
// Two queues, and the second is why this exists rather than a link to the
// central Enrollment Requests page:
//   • Requested this batch — students who named it.
//   • Open course applicants — students who applied to the course without
//     choosing a batch. They are invisible from here otherwise, yet they are
//     exactly who an admin filling the last four seats wants. Approving one
//     from this tab assigns THIS batch, which is the whole shortcut.
//
// Approve always posts this batch's id, never the request's stored batchId, so
// the button does what the tab it sits in says it does.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, Modal } from 'react-bootstrap';
import {
  CheckCircle2, XCircle, PauseCircle, Clock, Users, Info,
  AlertTriangle, CalendarDays, UserPlus, Inbox, ChevronDown, QrCode,
} from 'lucide-react';
import { AppButton, AppBadge, EmptyState } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import { hasPermission } from '../../utils/permissions';
import {
  getBatchEnrollmentRequests, approveRequest, holdRequest, rejectRequest,
} from '../../utils/enrollmentApi';
import './_enrollment-pages.scss';

const STATUS_CFG = {
  pending: { color: 'amber', label: 'Pending', icon: Clock },
  on_hold: { color: 'amber', label: 'On Hold', icon: PauseCircle },
  approved: { color: 'emerald', label: 'Approved', icon: CheckCircle2 },
  paid: { color: 'emerald', label: 'Paid', icon: CheckCircle2 },
  rejected: { color: 'rose', label: 'Rejected', icon: XCircle },
};

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—');

const emptyModal = { show: false, id: null, studentName: '', reason: '', kind: null };

function RequestRow({ r, actionable, busy, onApprove, onHold, onReject }) {
  const cfg = STATUS_CFG[r.status] || STATUS_CFG.pending;
  const StatusIcon = cfg.icon;
  const reason = r.status === 'on_hold' ? r.holdReason
    : r.status === 'rejected' ? r.rejectReason
      : '';

  return (
    <div className="enr-breq__row">
      <div className="enr-breq__who">
        <div className="enr-breq__avatar">{(r.studentId?.name || '?')[0].toUpperCase()}</div>
        <div className="enr-breq__who-text">
          <span className="enr-breq__name">{r.studentId?.name || '—'}</span>
          <span className="enr-breq__sub">{r.studentId?.email || ''}</span>
          {r.studentId?.phone && <span className="enr-breq__sub">{r.studentId.phone}</span>}
        </div>
      </div>

      <div className="enr-breq__meta">
        <span className="enr-breq__date"><CalendarDays size={13} /> {fmtDate(r.requestedAt || r.createdAt)}</span>
        {r.amountPaid > 0 && (
          <span className="enr-breq__paid">₹{Number(r.amountPaid).toLocaleString('en-IN')} paid</span>
        )}
        {r.enrollmentMode === 'request' && (
          <span className="enr-breq__tag">Approval required</span>
        )}
        {r.enrollmentMode === 'qr' && (
          <span className="enr-breq__tag">Paid by UPI QR</span>
        )}
      </div>

      <div className="enr-breq__status">
        <AppBadge color={cfg.color} size="sm"><StatusIcon size={12} /> {cfg.label}</AppBadge>
        {reason && <span className="enr-breq__reason" title={reason}>{reason}</span>}
      </div>

      <div className="enr-breq__actions">
        {actionable && r.enrollmentMode === 'qr' ? (
          // Decided by checking the payment, not here — the API refuses it.
          <AppButton as={Link} to="/payment-approvals" size="sm" variant="outline">
            <QrCode size={14} /> Verify UPI payment
          </AppButton>
        ) : actionable ? (
          <>
            <AppButton size="sm" variant="default" disabled={busy} onClick={() => onApprove(r)}>
              <CheckCircle2 size={14} /> Approve
            </AppButton>
            <AppButton size="sm" variant="outline" disabled={busy} onClick={() => onHold(r)}>
              <PauseCircle size={14} /> {r.status === 'on_hold' ? 'Update' : 'Hold'}
            </AppButton>
            <AppButton size="sm" variant="destructive" disabled={busy} onClick={() => onReject(r)}>
              <XCircle size={14} /> Reject
            </AppButton>
          </>
        ) : (
          <span className="enr-breq__sub">{r.resolvedBy?.name ? `by ${r.resolvedBy.name}` : '—'}</span>
        )}
      </div>
    </div>
  );
}

export default function BatchEnrollmentRequests({ batchId, onCountChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Approve / hold / reject need Add / Edit Students — the server's rule.
  const canDecide = hasPermission('add_edit_students');
  const [showResolved, setShowResolved] = useState(false);
  const [modal, setModal] = useState(emptyModal);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const load = useCallback(async () => {
    if (!batchId) return;
    try {
      setLoading(true);
      const res = await getBatchEnrollmentRequests(batchId);
      const d = res.data?.data || null;
      setData(d);
      onCountChange?.(d?.counts?.actionable || 0);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [batchId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  /* ── approve: always into THIS batch ── */
  const handleApprove = async (r) => {
    try {
      setBusyId(r._id);
      await approveRequest(r._id, { batchId });
      setToast({ show: true, message: `${r.studentId?.name || 'Student'} enrolled and notified.`, type: 'success' });
      await load();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to approve.', type: 'error' });
    } finally { setBusyId(null); }
  };

  const openModal = (kind) => (r) => setModal({
    show: true, kind, id: r._id,
    studentName: r.studentId?.name || 'Student',
    reason: kind === 'hold' ? (r.holdReason || '') : '',
  });

  const submitModal = async () => {
    const { id, kind, reason } = modal;
    if (!reason.trim()) {
      setToast({ show: true, message: 'Please give a reason — the student is emailed it.', type: 'error' });
      return;
    }
    try {
      setBusyId(id);
      if (kind === 'hold') await holdRequest(id, { holdReason: reason.trim() });
      else await rejectRequest(id, { rejectReason: reason.trim() });
      setToast({
        show: true,
        message: kind === 'hold' ? 'Request put on hold. Student notified.' : 'Request rejected. Student notified.',
        type: 'success',
      });
      setModal(emptyModal);
      await load();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Action failed.', type: 'error' });
    } finally { setBusyId(null); }
  };

  if (loading) {
    return (
      <div className="enr-breq">
        {[0, 1, 2].map(i => <div className="shimmer enr-breq__skel" key={i} />)}
      </div>
    );
  }

  if (!data) {
    return <EmptyState icon={<Inbox size={44} />} title="Couldn't load requests" description="Please refresh and try again." />;
  }

  const { direct = [], unassigned = [], resolved = [], batch = {}, counts = {} } = data;
  const nothingToDo = direct.length === 0 && unassigned.length === 0;

  return (
    <div className="enr-breq">
      {/* Seats first — it is the constraint every decision below is made against. */}
      <div className="enr-breq__summary">
        <div className="enr-breq__summary-item">
          <Users size={15} />
          <span><strong>{batch.strength || 0}</strong> enrolled</span>
        </div>
        {batch.seatsLeft !== null && batch.seatsLeft !== undefined && (
          <div className={`enr-breq__summary-item ${batch.seatsLeft === 0 ? 'is-full' : ''}`}>
            <UserPlus size={15} />
            <span><strong>{batch.seatsLeft}</strong> seats left</span>
          </div>
        )}
        <div className="enr-breq__summary-item">
          <Clock size={15} />
          <span><strong>{counts.actionable || 0}</strong> awaiting decision</span>
        </div>
      </div>

      {nothingToDo ? (
        <EmptyState
          icon={<Inbox size={44} />}
          title="No pending requests"
          description="Nobody is currently waiting for a seat in this batch."
        />
      ) : (
        <>
          {direct.length > 0 && (
            <section className="enr-breq__section">
              <h4 className="enr-breq__section-title">
                Requested this batch <span className="enr-breq__count">{direct.length}</span>
              </h4>
              <div className="enr-breq__list">
                {direct.map(r => (
                  <RequestRow
                    key={r._id} r={r} actionable={canDecide} busy={busyId === r._id}
                    onApprove={handleApprove} onHold={openModal('hold')} onReject={openModal('reject')}
                  />
                ))}
              </div>
            </section>
          )}

          {unassigned.length > 0 && (
            <section className="enr-breq__section">
              <h4 className="enr-breq__section-title">
                Open course applicants <span className="enr-breq__count">{unassigned.length}</span>
              </h4>
              <div className="enr-breq__hint">
                <Info size={14} />
                <span>These students applied to the course without picking a batch. Approving here assigns them to <strong>{batch.name}</strong>.</span>
              </div>
              <div className="enr-breq__list">
                {unassigned.map(r => (
                  <RequestRow
                    key={r._id} r={r} actionable={canDecide} busy={busyId === r._id}
                    onApprove={handleApprove} onHold={openModal('hold')} onReject={openModal('reject')}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {resolved.length > 0 && (
        <section className="enr-breq__section">
          <button
            type="button"
            className={`enr-breq__toggle ${showResolved ? 'is-open' : ''}`}
            onClick={() => setShowResolved(v => !v)}
            aria-expanded={showResolved}
          >
            <ChevronDown size={15} />
            {showResolved ? 'Hide' : 'Show'} past decisions ({resolved.length})
          </button>
          {showResolved && (
            <div className="enr-breq__list enr-breq__list--muted">
              {resolved.map(r => (
                <RequestRow key={r._id} r={r} actionable={false} busy={false}
                  onApprove={() => {}} onHold={() => {}} onReject={() => {}} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* One modal for both reason-bearing decisions — same field, same rule. */}
      <Modal show={modal.show} onHide={() => setModal(emptyModal)} centered dialogClassName="enr-modal">
        <div className={`enr-modal__header ${modal.kind === 'hold' ? 'enr-modal__header--warn' : 'enr-modal__header--danger'}`}>
          <div className="enr-modal__header-icon">
            {modal.kind === 'hold' ? <PauseCircle size={22} /> : <AlertTriangle size={22} />}
          </div>
          <div>
            <h5 className="enr-modal__title">
              {modal.kind === 'hold' ? 'Put Request On Hold' : 'Reject Request'}
            </h5>
            <p className="enr-modal__subtitle">
              {modal.kind === 'hold' ? 'Pausing the decision for ' : 'Declining '}
              <strong>{modal.studentName}</strong>
            </p>
          </div>
          <button className="enr-modal__close" onClick={() => setModal(emptyModal)}>&times;</button>
        </div>
        <div className="enr-modal__body">
          <div className={`enr-modal__info-box ${modal.kind === 'hold' ? '' : 'enr-modal__info-box--warn'}`}>
            <Info size={15} />
            <span>
              {modal.kind === 'hold'
                ? 'The request stays open — you can approve or reject it later. The student is emailed the reason below.'
                : 'The student is emailed this decision along with your reason. They can apply again afterwards.'}
            </span>
          </div>
          <label className="enr-modal__label" htmlFor="breq-reason">
            Reason <span className="enr-modal__required">(required)</span>
          </label>
          <textarea
            id="breq-reason"
            className="enr-modal__textarea"
            rows={4}
            value={modal.reason}
            onChange={e => setModal(prev => ({ ...prev, reason: e.target.value }))}
            placeholder={modal.kind === 'hold'
              ? 'e.g. Waiting on documents, seat confirmation pending…'
              : 'e.g. Prerequisites not met, batch is full…'}
          />
        </div>
        <div className="enr-modal__footer">
          <AppButton variant="secondary" onClick={() => setModal(emptyModal)}>Cancel</AppButton>
          <AppButton
            variant={modal.kind === 'hold' ? 'default' : 'destructive'}
            onClick={submitModal}
            disabled={busyId === modal.id || !modal.reason.trim()}
          >
            {busyId === modal.id
              ? <><Spinner size="sm" animation="border" /> Saving…</>
              : modal.kind === 'hold' ? <><PauseCircle size={14} /> Put On Hold</> : <><XCircle size={14} /> Reject</>}
          </AppButton>
        </div>
      </Modal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
}
